import type { SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, Input } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import { filterBoolean, trackByProp } from '@core/common-legacy';
import { OfferStatus } from '@core/offers-types';
import type { AssignedJob } from '@recruitee/requisitions-data';

import type { RequisitionsJobItem } from '../../../components/requisitions-job-item/requisitions-job-item.component';
import { RequisitionsJobItemComponent } from '../../../components/requisitions-job-item/requisitions-job-item.component';
import { RequisitionsCommonTextsService } from '../../../services/requisitions-common-texts.service';

@Component({
  selector: 'rt-requisitions-jobs-list-container',
  template: `
    @for (item of items; track trackById($index, item)) {
      <rt-requisitions-job-item [item]="item"></rt-requisitions-job-item>
    }
  `,
  styleUrls: ['./requisitions-jobs-list-container.component.less'],
  imports: [RequisitionsJobItemComponent],
})
export class RequisitionsJobsListContainerComponent {
  @Input() public jobs: AssignedJob[] = [];

  public trackById = trackByProp<{ id: number }>({ prop: 'id' });

  public items: RequisitionsJobItem[] = [];
  private ngOnDestroy$ = new Subject<void>();
  private canSeeJobsSection = false;
  private canSeeAllJobs = false;
  private assignedJobIds: number[] = [];

  constructor(
    private store: Store,
    private requisitionsCommonTexts: RequisitionsCommonTextsService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(currentUser => {
        this.assignedJobIds = currentUser.membership?.offerIds || [];
        this.canSeeAllJobs = currentUser.roleAbilities.includes(Ability.ViewAllJobs);
        this.canSeeJobsSection = currentUser.roleAbilities.includes(Ability.ViewJobs);
        if (this.jobs) {
          this.mapItems();
          this.cdRef.detectChanges();
        }
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.jobs) {
      this.mapItems();
    }
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private mapItems() {
    this.items = this.jobs.map(job => ({
      ...job,
      guid: job.guid ?? '',
      department: job.department ?? null,
      qualifiedCount: job.qualifiedCandidatesCount || 0,
      hiredCount: job.hiredCandidatesCount || 0,
      disqualifiedCount: job.disqualifiedCandidatesCount || 0,
      lastActivityAt: job.updatedAt,
      url: this.resolveUrl(job),
      location: this.requisitionsCommonTexts.getLocationText(job),
    }));
  }

  private resolveUrl(job: AssignedJob): string {
    const hasAccess =
      this.canSeeAllJobs || (this.canSeeJobsSection && this.assignedJobIds.includes(job.id));
    if (!hasAccess) return '';

    let section = '';
    switch (job.status) {
      case OfferStatus.Draft:
        section = 'edit';
        break;
      default:
        section = 'pipeline';
        break;
    }
    return `#/offers/${job.slug}/${section}`;
  }
}
